import { useEffect, useRef, useState } from 'preact/hooks'
import { ApiError, type ErrorMessages, errorMessage } from '../api/client'
import type { Me } from '../api/generated/Me'
import { conditionalLoginAvailable, isCancelled, loginWithPasskey } from './passkey'

const messages: ErrorMessages = { challenge_expired: 'La demande a expiré. Réessayez.' }

/**
 * Logs in with a passkey, from the autofill of a field with `autocomplete="… webauthn"` while
 * mounted, or from `start()`.
 */
export function usePasskeyLogin(onSignedIn: (me: Me) => void) {
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  const autofill = useRef<AbortController>(undefined)
  const mounted = useRef(true)

  const fail = (e: unknown) => setError(errorMessage(e, messages))

  // Only one WebAuthn request may be pending: the button aborts this one, then restarts it
  const listenToAutofill = async () => {
    if (!(await conditionalLoginAvailable()) || !mounted.current) return
    const controller = new AbortController()
    autofill.current = controller
    try {
      onSignedIn(await loginWithPasskey('conditional', controller.signal))
    } catch (e) {
      if (controller.signal.aborted || isCancelled(e)) return
      fail(e)
      // A chosen passkey was refused: let the user pick again
      if (e instanceof ApiError && (e.code === 'challenge_expired' || e.code === 'invalid_passkey'))
        listenToAutofill()
    }
  }

  useEffect(() => {
    listenToAutofill()
    return () => {
      mounted.current = false
      autofill.current?.abort()
    }
  }, [])

  const start = async () => {
    autofill.current?.abort()
    setError(undefined)
    setPending(true)
    try {
      onSignedIn(await loginWithPasskey())
    } catch (e) {
      if (!isCancelled(e)) fail(e)
      listenToAutofill()
    } finally {
      setPending(false)
    }
  }

  return { error, pending, start }
}
